import React, { useState, useEffect } from 'react';
import { videoAPI } from '../utils/api';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  ArrowLeft, Crown, TrendingUp, Heart, Film,
  Loader2, Play, Award, Star, Flame
} from 'lucide-react';

const BADGE_CONFIG = {
  top_creator: { label: 'Top Creator', icon: Crown, color: '#EA580C', bg: '#FFF7ED' },
  rising_star: { label: 'Rising Star', icon: Star, color: '#7C3AED', bg: '#F5F3FF' },
  trending: { label: 'Trending', icon: Flame, color: '#E11D48', bg: '#FFF1F2' },
};

const Leaderboard = ({ onBack, onOpenGallery }) => {
  const [creators, setCreators] = useState([]);
  const [trending, setTrending] = useState([]);
  const [tab, setTab] = useState('creators');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [lb, tr] = await Promise.all([
        videoAPI.getLeaderboard(),
        videoAPI.getTrending(),
      ]);
      setCreators(lb.data.creators);
      setTrending(tr.data.videos);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const Badge = ({ type }) => {
    if (!type || !BADGE_CONFIG[type]) return null;
    const cfg = BADGE_CONFIG[type];
    const Icon = cfg.icon;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
        <Icon className="w-3 h-3" /> {cfg.label}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#F8FAFC]" data-testid="leaderboard">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-white border-b border-[#E2E8F0] shadow-sm">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="leaderboard-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#EA580C] to-[#E11D48] flex items-center justify-center">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-[#0F172A] text-lg leading-tight">Leaderboard</h1>
            <p className="text-xs text-[#64748B]">Top creators & trending</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E2E8F0] bg-white">
        <button onClick={() => setTab('creators')} data-testid="tab-creators"
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors border-b-2 ${
            tab === 'creators' ? 'text-[#EA580C] border-[#EA580C]' : 'text-[#64748B] border-transparent hover:text-[#334155]'
          }`}>
          <Crown className="w-4 h-4" /> Top Creators
        </button>
        <button onClick={() => setTab('trending')} data-testid="tab-trending"
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors border-b-2 ${
            tab === 'trending' ? 'text-[#E11D48] border-[#E11D48]' : 'text-[#64748B] border-transparent hover:text-[#334155]'
          }`}>
          <TrendingUp className="w-4 h-4" /> Trending Videos
        </button>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#4338CA] animate-spin" />
          </div>
        ) : tab === 'creators' ? (
          <div className="p-4 space-y-2" data-testid="creators-list">
            {creators.length === 0 && (
              <p className="text-sm text-[#94A3B8] text-center py-12">No creators yet. Be the first to share a public video!</p>
            )}
            {creators.map((c, i) => (
              <div key={c.user_id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                i === 0 ? 'bg-gradient-to-r from-[#FFF7ED] to-white border-[#FDBA74] shadow-sm' : 'bg-white border-[#E2E8F0]'
              }`} data-testid={`creator-${c.rank}`}>
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${
                  i === 0 ? 'bg-[#EA580C] text-white' : i === 1 ? 'bg-[#64748B] text-white' : i === 2 ? 'bg-[#B45309] text-white' : 'bg-[#F1F5F9] text-[#64748B]'
                }`}>
                  {c.rank}
                </div>

                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4338CA] to-[#7C3AED] flex items-center justify-center text-white font-bold text-sm shrink-0">
                  {c.user_name?.charAt(0).toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0F172A] text-sm truncate">{c.user_name}</span>
                    <Badge type={c.badge} />
                  </div>
                  <p className="text-xs text-[#64748B]">{c.video_count} video{c.video_count !== 1 ? 's' : ''}</p>
                </div>

                {/* Likes */}
                <div className="flex items-center gap-1 text-sm font-semibold text-[#E11D48] shrink-0">
                  <Heart className="w-4 h-4 fill-[#E11D48]" />
                  {c.total_likes}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 space-y-3" data-testid="trending-list">
            {trending.length === 0 && (
              <p className="text-sm text-[#94A3B8] text-center py-12">No trending videos this week. Share a video and get likes!</p>
            )}
            {trending.map(v => (
              <div key={v.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm" data-testid={`trending-${v.id}`}>
                {v.video_url && (
                  <div className="relative bg-black">
                    <video src={v.video_url} controls className="w-full max-h-48 object-contain" preload="metadata" />
                    {v.has_watermark && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 rounded text-[10px] text-white/70 font-medium">
                        CallChat AI
                      </div>
                    )}
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-[#0F172A]">{v.user_name || 'Anonymous'}</span>
                    <Badge type={v.badge} />
                    <div className="flex items-center gap-1 ml-auto text-sm text-[#E11D48]">
                      <Heart className="w-3.5 h-3.5 fill-[#E11D48]" /> {v.like_count || 0}
                    </div>
                  </div>
                  <p className="text-xs text-[#475569] line-clamp-2">{v.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default Leaderboard;
